import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { ArrowUpRight, ArrowLeft, Locate } from "lucide-react";
import { formatNumber } from "../utils/statsUtils";
import TopDaysChart from "./TopDays";
// import TopSkiedRunsChart from "./TopSkiedRuns";

const Header = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;

  @media (max-width: 800px) {
    position: sticky;
    top: 0;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    padding: 16px 0;
    margin: -16px -16px 16px -16px;
    padding-left: 16px;
    padding-right: 16px;
    z-index: 10;
    border-bottom: 1px solid #eee;
  }
`;

const RegionName = styled.div`
  font-size: 1.5rem;
  font-weight: 800;
  color: #111;
`;

const Location = styled.div`
  font-size: 0.9rem;
  color: #888;
  margin-top: 2px;
`;

const StyledLocate = styled(Locate)`
  width: 16px;
  height: 16px;
  color: #999;
  margin-top: 4px;
  transition: color 0.2s ease;
`;

const StyledArrow = styled(ArrowUpRight)`
  width: 20px;
  height: 20px;
  color: #999;
  margin-top: 4px;
  transition: color 0.2s ease;
`;

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  border-top: 1px solid #eee;
  padding: 12px 4px;
  cursor: ${({ $isClickable }) => ($isClickable ? "pointer" : "default")};
  margin: 2px 0;

  &:hover ${StyledLocate}, &:hover ${StyledArrow} {
    color: #005fcc;
  }
`;

const StatInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatValue = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  color: #111;
`;

const StatLabel = styled.div`
  font-size: 0.8rem;
  color: #555;
  margin-top: 2px;
`;

const BackButton = styled(ArrowLeft)`
  color: #999;
  width: 18px;
  height: 18px;
  margin-bottom: 8px;
  cursor: pointer;
  &:hover {
    color: #005fcc;
  }
`;

export function useAnimatedNumber(targetNumber, duration = 800) {
  const [animatedNumber, setAnimatedNumber] = useState(
    formatNumber(targetNumber ?? 0)
  );
  const previousNumber = useRef(targetNumber ?? 0);

  useEffect(() => {
    let start;
    let frame;

    const step = (timestamp) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);

      // Animate from previous number to target number
      const current =
        previousNumber.current +
        ((targetNumber ?? 0) - previousNumber.current) * progress;
      setAnimatedNumber(formatNumber(Math.round(current)));

      if (progress < 1) {
        frame = requestAnimationFrame(step);
      } else {
        previousNumber.current = targetNumber ?? 0;
      }
    };

    if (previousNumber.current !== (targetNumber ?? 0)) {
      frame = requestAnimationFrame(step);
    }

    return () => {
      if (frame) {
        cancelAnimationFrame(frame);
      }
    };
  }, [targetNumber, duration]);

  return animatedNumber;
}

export default function RegionDetail({
  regionMetaData,
  regionStats,
  setSelectedRegionId,
  onHoverLocation,
  regionPOIs,
}) {
  const animatedTotalDistance = useAnimatedNumber(regionStats.totalDistance);
  const animatedTotalVertical = useAnimatedNumber(regionStats.totalVertical);
  const animatedTotalDays = useAnimatedNumber(regionStats.totalDays);
  const animatedLongestHike = useAnimatedNumber(regionStats.longestHike);

  const [showTopDistance, setShowTopDistance] = useState(false);
  const [showTopVertical, setShowTopVertical] = useState(false);

  const handleLongestHikeHover = (isHovering) => {
    if (isHovering && regionStats.longestHikeLocation) {
      const [lng, lat] = regionStats.longestHikeLocation.coordinates;
      onHoverLocation({
        lng,
        lat,
        segments: regionStats.longestHikeLocation.segments,
      });
    } else {
      onHoverLocation(null);
    }
  };

  return (
    <>
      <Header>
        <BackButton
          onClick={() => {
            if (showTopDistance) {
              setShowTopDistance(false);
            } else if (showTopVertical) {
              setShowTopVertical(false);
            } else {
              setSelectedRegionId(null);
            }
          }}
        />
        <RegionName>{regionMetaData?.name}</RegionName>
        <Location>{regionMetaData?.location}</Location>
      </Header>
      <>
        {!showTopVertical && !showTopDistance && (
          <>
            <StatRow $isClickable={false}>
              <StatInfo>
                <StatValue>{animatedTotalDays}</StatValue>
                <StatLabel>hiking days</StatLabel>
              </StatInfo>
            </StatRow>
            <StatRow
              onClick={() => {
                setShowTopDistance(true);
              }}
              $isClickable={true}
            >
              <StatInfo>
                <StatValue>{animatedTotalDistance}</StatValue>
                <StatLabel>km</StatLabel>
              </StatInfo>
              <StyledArrow />
            </StatRow>
            <StatRow
              onClick={() => {
                setShowTopVertical(true);
              }}
              $isClickable={true}
            >
              <StatInfo>
                <StatValue>{animatedTotalVertical}</StatValue>
                <StatLabel>vertical meter</StatLabel>
              </StatInfo>
              <StyledArrow />
            </StatRow>
            <StatRow
              $isClickable={false}
              onMouseEnter={() => handleLongestHikeHover(true)}
              onMouseLeave={() => handleLongestHikeHover(false)}
            >
              <StatInfo>
                <StatValue>{animatedLongestHike}</StatValue>
                <StatLabel>longest hike (km)</StatLabel>
              </StatInfo>
              <StyledLocate />
            </StatRow>
          </>
        )}
        {showTopDistance && (
          <TopDaysChart data={regionStats.distancePerDay} unit="km" />
        )}
        {showTopVertical && (
          <TopDaysChart data={regionStats.verticalPerDay} unit="m" />
        )}
      </>
    </>
  );
}
